import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useMatch, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../../assets/images/logo.png";
import { userAuth, userService } from "../../services/userApi";
import socketService from "../../services/SocketService";
import "../lawyer/lawyer.css";
import "./user.css";

const NAV_ITEMS = [
  { to: "/user/dashboard", label: "Home", icon: "bi-house-door-fill" },
  { to: "/user/call-history", label: "Call History", icon: "bi-telephone-inbound-fill" },
  { to: "/user/chat", label: "Chat", icon: "bi-chat-dots-fill", badgeKey: "chat_unread" },
  { to: "/user/requests", label: "My Request", icon: "bi-clipboard2-check-fill", badgeKey: "answers_unread" },
  { to: "/user/profile", label: "Profile", icon: "bi-person-fill" },
];

// Shell for every signed-in user page: sidebar on desktop, floating bottom
// bar on mobile. The profile is loaded once here and shared via Outlet context.
const UserLayout = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(userAuth.getUser());

  const refreshProfile = useCallback(async () => {
    try {
      const res = await userService.getProfile();
      if (res?.data?.status) {
        setProfile(res.data.data);
        userAuth.setUser(res.data.data);
      }
    } catch {
      // 401s are handled by the interceptor; keep the cached profile otherwise.
    }
  }, []);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  // Register this user with the socket server (and again after every reconnect)
  // so chat messages, read receipts, presence and call answers reach this tab.
  const userId = profile?.id;
  const userName = profile?.name;
  useEffect(() => {
    if (!userId) return undefined;
    const announce = () => socketService.emit("user-connected", { id: userId, name: userName });
    announce();
    const offConnect = socketService.subscribe("connect", announce);
    // Keeps the Chat badge current; the open conversation marks its own reads.
    const offMessage = socketService.subscribe("receive-message", () => setTimeout(refreshProfile, 800));
    return () => {
      offConnect();
      offMessage();
    };
  }, [userId, userName, refreshProfile]);

  // An open conversation takes the full screen on mobile (no bottom bar); a
  // lawyer's profile pins its own "Get Service" bar there instead.
  const inChat = !!useMatch("/user/chat/:chatId");
  const inLawyer = !!useMatch("/user/lawyers/:lawyerId");
  const inConversation = inChat || inLawyer;

  const logout = async () => {
    try {
      await userService.logout();
    } catch {
      // Clear the local session even if the server call fails.
    }
    userAuth.clear();
    toast.success("Logged out successfully");
    navigate("/user/login", { replace: true });
  };

  return (
    <div className={`lwp lwp-shell usp${inConversation ? " lwp-in-conversation" : ""}`}>
      <aside className="lwp-sidebar">
        <img src={logo} alt="Legal Platform" className="lwp-sidebar-logo" />
        <nav className="lwp-sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className="lwp-sidebar-link">
              <i className={`bi ${item.icon}`} />
              <span>{item.label}</span>
              {item.badgeKey && profile?.[item.badgeKey] > 0 && (
                <span className="lwp-count">{profile[item.badgeKey]}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <button type="button" className="lwp-sidebar-link lwp-logout" onClick={logout}>
          <i className="bi bi-box-arrow-right" />
          <span>Logout</span>
        </button>
      </aside>

      <main className="lwp-main">
        <Outlet context={{ profile, setProfile, refreshProfile, logout }} />
      </main>

      <nav className="lwp-bottom-nav">
        {NAV_ITEMS.map((item) => (
          <NavLink key={item.to} to={item.to} className="lwp-bottom-link">
            <span className="lwp-bottom-icon">
              <i className={`bi ${item.icon}`} />
              {item.badgeKey && profile?.[item.badgeKey] > 0 && (
                <span className="lwp-count">{profile[item.badgeKey]}</span>
              )}
            </span>
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default UserLayout;
